"use client";

import { useState, useCallback, useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SearchInputProps {
    initialQuery?: string;
    placeholder?: string;
    className?: string;
    autoFocus?: boolean;
}

export default function SearchInput({
    initialQuery = '',
    placeholder = 'What are you looking for?',
    className,
    autoFocus = false,
}: SearchInputProps) {
    const router = useRouter();
    const searchParams = useSearchParams();
    // Initialize from URL param or prop
    const [query, setQuery] = useState(() => searchParams.get('q') || initialQuery);
    const inputRef = useRef<HTMLInputElement>(null);

    // Auto-focus if requested
    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    const handleSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = query.trim();
        if (trimmed) {
            router.push(`/search?q=${encodeURIComponent(trimmed)}`);
        } else {
            router.push('/search');
        }
    }, [query, router]);

    return (
        <form onSubmit={handleSubmit} role="search" aria-label="Site search" className={cn("relative", className)}>
            <div className="relative">
                <label htmlFor="site-search" className="sr-only">Search</label>
                <input
                    ref={inputRef}
                    id="site-search"
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={placeholder}
                    className="w-full h-14 pl-5 pr-14 text-lg bg-background border-2 border-border rounded-xl focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    aria-label="Search"
                />
                <button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    aria-label="Submit search"
                >
                    <Search className="w-5 h-5" />
                </button>
            </div>
        </form>
    );
}
